import {Injectable} from '@angular/core';
import {TenantsRestService} from '../apis/tenants-rest.service';
import {ChannelsRestService} from '../apis/channels-rest.service';
import {LCCustomersRestService} from '../apis/lc-customers-rest.service';
import {LinksRestService} from '../apis/links-rest.service';
import {DevicesRestService} from '../apis/devices-rest.service';
import {ArrayUtil} from 'ac-infra';
import {GroupsRestService} from '../apis/groups-rest.service';
import {ConstantStringsService} from '../../../common/utilities/constant-strings.service';
import {NetworkBaseTreeBuilderService, TreeEntitiesInstructions} from './network-base-tree-builder.service';
import {WsEntitiesService} from '../../../common/services/communication/ws-entities.service';


export type TreeEntityTypeLiveCloud = 'tenant' | 'channel' | 'lcCustomer' | 'service' | 'siteLocation' | 'link' | 'device';
export const DEFAULT_ENTITY_TYPES_LIST: TreeEntityTypeLiveCloud[] = ['tenant', 'channel', 'lcCustomer', 'service', 'device', 'link'];

@Injectable({providedIn: 'root'})
export class NetworkLiveCloudDataService extends NetworkBaseTreeBuilderService<TreeEntityTypeLiveCloud> {

    static WS_ENTITY_TYPE_MAP = {
        customer: 'lcCustomer',
    };

    treeEntitiesInstructions: TreeEntitiesInstructions<TreeEntityTypeLiveCloud>;

    constructor(private tenantsRestService: TenantsRestService,
                private channelsRestService: ChannelsRestService,
                private lcCustomersRestService: LCCustomersRestService,
                private linksRestService: LinksRestService,
                private devicesRestService: DevicesRestService,
                private groupsRestService: GroupsRestService,
                private wsEntitiesService: WsEntitiesService,) {

        super(DEFAULT_ENTITY_TYPES_LIST, {
            tenant: {
                getEntities: (tenantId) => this.tenantsRestService.getEntitiesByTenantId(tenantId, {hashed: true})
            },
            channel: {
                parents: ['tenant'],
                artificialParentType: 'tenant',
                getParentId: (entity: any) => entity.tenantId,
                getEntities: (tenantId) => this.channelsRestService.getEntitiesByTenantId(tenantId, {hashed: true})
            },
            lcCustomer: {
                parents: ['tenant', 'channel'],
                artificialParentType: 'tenant',
                artificialName: 'Customers',
                getParentId: (entity: any) => {
                    if (!!entity.channelId && entity.channelId >= 0) {
                        return entity.channelId;
                    }
                    return entity.tenantId;
                },
                getEntities: (tenantId) => this.lcCustomersRestService.getEntitiesByTenantId(tenantId, {hashed: true})
            },
            service: {
                parents: ['lcCustomer', 'tenant'],
                artificialParentType: 'tenant',
                artificialName: 'Services',
                iconNameField: 'serviceSource',
                getParentId: (entity: any) => (entity.parentType === 'Customer' || entity.parentType === 'Tenant') ? entity.parentId : entity.lcCustomerId,
                getEntities: (tenantId) => this.groupsRestService.getEntitiesByTenantId(tenantId, {hashed: true, entityType: 'services'}),
            },
            device: {
                parents: ['service'],
                artificialParentType: 'service',
                artificialName: 'Devices',
                getParentId: (entity: any) => this.getDeviceServiceId(entity),
                getEntities: (tenantId, entityFilter) => this.devicesRestService.getEntitiesByTenantId(tenantId, {
                    hashed: true,
                    entityFilter: (device) => this.getDeviceServiceId(device) != null && (!entityFilter || entityFilter(device)),
                }),
            },
            link: {
                parents: ['service'],
                artificialParentType: 'service',
                getParentId: (entity: any) => {
                    const [serviceId] = entity.assignedGroups || [];
                    return serviceId;
                },
                artificialName: () => ConstantStringsService.linkOrProviderSideMany,
                getEntities: (tenantId) => this.linksRestService.getEntitiesByTenantId(tenantId, {hashed: true}),
            },
        });
    }

    getIconFieldMap() {
        return ArrayUtil.arrayToObject(Object.entries(this.treeEntitiesInstructions), (acc, [key, value]) => {
            if (value.iconNameField) {
                acc[key] = value.iconNameField;
            }
        });
    }

    // A hybrid device references its parent service directly (serviceId, or assignedGroups[0] in OneLive),
    // so it is self-contained for the tree and for live WS updates - no need to reverse-scan the service's
    // attachedDevices (which would depend on the services collection being WS-refreshed in the current view).
    getDeviceServiceId = (device: any) => device.serviceId || device.assignedGroups?.[0];

    isArtificialHybrid = (artificialEntity: any, parent, children, isHybrid: (entity: any) => boolean) => {
        const entities = this.wsEntitiesService.getAnyEntitiesByIds(children) as any[];

        let isArtificialHybrid = false;
        entities.forEach((entity) => {
            isArtificialHybrid = isArtificialHybrid || isHybrid(entity);
        });

        return isArtificialHybrid;
    };

    isChannelHybrid = (entity: any) => {
        return !!(entity.services?.HYBRID_ENTITIES.enabled || entity.services?.LIVEOPS_360_MANAGEMENT.enabled);
    };

    isLcCustomerHybrid = (entity: any) => {
        return !!(entity.services?.HYBRID_ENTITIES || entity.services?.LIVEOPS_360_MANAGEMENT);
    };

    isServiceHybrid = (entity: any) => {
        return (entity.serviceSource === 'HYBRID_ENTITIES' || entity.serviceSource === 'LIVEOPS_360_MANAGEMENT');
    };

    isHybrid = (entity: any, parent: any, children: any[], isHybrid: (entity: any) => boolean) => {
        if (entity.artificial) {
            return this.isArtificialHybrid(entity, parent, children, isHybrid);
        }
        return isHybrid(entity);
    };

    getHybridFilters = (needFilter: boolean) => {
        if (!needFilter) {
            return;
        }
        return {
            channel: (entity: any, parent: any, children: any[]) => this.isHybrid(entity, parent, children, this.isChannelHybrid),
            lcCustomer: (entity: any, parent: any, children: any[]) => this.isHybrid(entity, parent, children, this.isLcCustomerHybrid),
            service: (entity: any, parent: any, children: any[]) => this.isHybrid(entity, parent, children, this.isServiceHybrid),
        };
    };
}
